import React, { Fragment, useEffect, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { SearchForm } from './SearchForm';

type Invoice = {
  invoiceNumber: number
  invoiceDisplayName: string
  outstandingAmount: number
  totalAmount: number
  selected: boolean
  expanded: boolean
  lineDetails: InvoiceLineDetail[]
  hasInstallmentPlan: boolean
}

type InvoiceLineDetail = {
  id: number
  amount: number
  date: Date
  description: string
}

type AppProps = {
  isMobile: boolean
}

type AppState = {
  groupId?: string
  loading?: boolean
  billingDisplayName?: string
  invoices: Invoice[]
  totalAmount: number
  paypalInitComplete: boolean
  paypalActions?: any
  invoiceSelectionError: boolean
  generalError: string
  successMessage: string
  width: number
  isMobile: boolean
  allSelected: boolean
  allExpanded: boolean
  loadingLines: number
  interactionDisabled: boolean
  preview: boolean
}

const Table = styled.table`
  border-collapse: separate;
`;

const Tr = styled.tr<{ isDetailLine?: boolean, grayBg?: boolean, whiteBg?: boolean, topBorder?: boolean, bottomBorder?: boolean }>(({ isDetailLine, grayBg, whiteBg }) => [
  css`
    border-color: black;
    border-style: solid;
    border-width: 0px;

    &:last-child td {
      border-bottom-width: 1px;
    }

    td,
    th {
      vertical-align: middle;
      padding: 15px;

      @media (max-width: 768px) {
        padding: 5px;
      }
    }
  `,
  isDetailLine && css`
    td,
    th {
      padding: 6px;
      background: none !important;

      @media (max-width: 768px) {
        padding: 3px;
      }
    }
  `,
  grayBg && css`
    background-color: #f7f7f7 !important;
    td {
      background-color: transparent !important;
    }
  `,
  whiteBg && css`
    background-color: #ffffff !important;
    td {
      background-color: transparent !important;
    }
  `
]);

const Th = styled.th`
  border-left-width: 0px;
  border-bottom-width: 0px;

  &:first-child {
    border-left-width: 1px;
  }
`;

const Td = styled.td<{ noTopBorder?: boolean, noBottomBorder?: boolean, topBorder?: boolean, bottomBorder?: boolean, leftBorder?: boolean, rightBorder?: boolean, subTitle?: boolean }>
  (({ noTopBorder, noBottomBorder, topBorder, bottomBorder, leftBorder, rightBorder, subTitle }) => [
  css`
    border-left-width: 0px;
    border-bottom-width: 0px;

    &:first-child {
      border-left-width: 1px;
    }
  `,
  noTopBorder && css`
    border-top: none !important;
  `,
  noBottomBorder && css`
    border-bottom: none !important;
  `,
  topBorder && css`
    border-top-width: 2px !important;
    border-top-color: black !important;
  `,
  bottomBorder && css`
    border-bottom-width: 2px !important;
    border-bottom-color: black !important;
  `,
  leftBorder && css`
    border-left-width: 1px !important;
    border-left-color: black !important;
  `,
  rightBorder && css`
    border-right-width: 1px !important;
    border-right-color: black !important;
  `,
  subTitle && css`
    font-size: 1.1rem;
    padding: 10px !important;
  `
]);


const shimmerAnimation = keyframes`
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
`;

const Shimmer = styled.div`
  height: 1rem;
  background: #777;

  animation : ${shimmerAnimation} 2s infinite linear;
  background: linear-gradient(to right, #eff1f3 4%, #e2e2e2 25%, #eff1f3 36%);
  background-size: 1000px 100%;
`;


export const useMediaQuery = (query: string) => {
  const mediaMatch = window.matchMedia(query);
  const [matches, setMatches] = useState(mediaMatch.matches);

  useEffect(() => {
    const handler = (e: any) => setMatches(e.matches);
    mediaMatch.addEventListener('change', handler);
    return () => mediaMatch.removeEventListener('change', handler);
  });
  return matches;
};

class App extends React.Component<AppProps, AppState> {

  constructor(props: any) {
    super(props);

    const urlParams = new URLSearchParams(window.location.search);
    const groupId = urlParams.get('id') || '';
    let loadingLines = +(urlParams.get('lines') || '1');
    loadingLines = Math.min(Math.max(loadingLines, 1), 10);
    const defaultState = {
      generalError: '',
      groupId: null,
      totalAmount: 0,
      invoices: [],
      loading: true,
      paypalInitComplete: false,
      invoiceSelectionError: false,
      width: 0,
      successMessage: '',
      isMobile: false,
      allSelected: false,
      allExpanded: false,
      loadingLines: 1,
      interactionDisabled: false,
      preview: urlParams.get('preview') === '1'
    };

    this.state = Object.assign(
      {},
      defaultState,
      {
        groupId,
        loadingLines
      }
    );

    this.handleSelectedChange = this.handleSelectedChange.bind(this);
    this.handleIdChange = this.handleIdChange.bind(this);
    this.toggleAll = this.toggleAll.bind(this);
    this.expandAll = this.expandAll.bind(this);
    this.resetState = this.resetStatePreset.bind(this, defaultState);
    this.handleHistoryChange = this.handleHistoryChange.bind(this);
  }

  async loadInvoices() {
    try {
      const result = await fetch(`${process.env.REACT_APP_BACKEND_URL}/ags/one-time-payment/${this.state.groupId}${this.state.preview ? '?preview=1' : ''}`);
      const json = await result.json();
      if (json.error) {
        this.setState({
          generalError: json.message
        });
        return;
      }
      if (json.redirectId) {
        return this.handleIdChange(json.redirectId, true);
      }
      this.setState({
        loading: false,
        billingDisplayName: json.billingDisplayName,
        invoices: json.invoices.map((i: Invoice): Invoice => ({
          ...i,
          selected: false,
          expanded: false,
          lineDetails: i.lineDetails.map((d: InvoiceLineDetail): InvoiceLineDetail => ({
            ...d,
            date: d.date ? new Date(d.date) : d.date,
          })).sort((a, b) => +a.date - +b.date)
        }))
      });
      this.initPayPalButton();
    } catch (error) {
      console.error(error);
      this.setState({
        generalError: 'Internal error, please try again later or contact support'
      });
    }
  }

  handleSelectedChange(invoiceNumber: number, event: React.ChangeEvent<HTMLInputElement>) {
    const invoices = [...this.state.invoices];
    for (const i of invoices) {
      if (i.invoiceNumber === invoiceNumber) {
        i.selected = event.target.checked;
        break;
      }
    }

    this.recalculateInvoiceTotals(invoices);
  }

  toggleInvoice(invoiceNumber: number) {
    const invoices = [...this.state.invoices];
    for (const i of invoices) {
      if (i.invoiceNumber === invoiceNumber) {
        i.selected = !i.selected;
        break;
      }
    }

    const allSelected = this.state.invoices.filter(i => i.selected).length === this.state.invoices.length;
    this.setState({ allSelected }, () => {
      this.recalculateInvoiceTotals(invoices);
    })
  }

  expandInvoice(invoiceNumber: number) {
    const invoices = [...this.state.invoices];
    for (const i of invoices) {
      if (i.invoiceNumber === invoiceNumber) {
        i.expanded = !i.expanded;
        break;
      }
    }

    const allExpanded = this.state.invoices.filter(i => i.expanded).length === this.state.invoices.length;
    this.setState({ invoices, allExpanded });
  }

  toggleAll() {
    const allSelected = this.state.invoices.filter(i => i.selected).length === this.state.invoices.length;
    const invoices = [...this.state.invoices];
    for (const i of invoices) {
      i.selected = !allSelected;
    }

    this.setState({ allSelected: !allSelected }, () => {
      this.recalculateInvoiceTotals(invoices);
    });
  }

  expandAll() {
    const allExpanded = this.state.invoices.filter(i => i.expanded).length === this.state.invoices.length;
    const invoices = [...this.state.invoices];
    for (const i of invoices) {
      i.expanded = !allExpanded;
    }

    this.setState({ invoices, allExpanded: !allExpanded });
  }

  recalculateInvoiceTotals(invoices: Invoice[]) {
    const totalAmount = invoices.filter(i => i.selected).reduce((acc: number, i) => acc + i.outstandingAmount, 0);
    totalAmount > 0 ? this.state.paypalActions.enable() : this.state.paypalActions.disable()
    this.setState(prevState => ({
      invoices,
      totalAmount,
      invoiceSelectionError: totalAmount > 0 ? false : prevState.invoiceSelectionError
    }));
  }

  componentDidMount() {
    window.addEventListener('popstate', this.handleHistoryChange);
    if (this.state.groupId && !this.state.generalError) {
      this.loadInvoices();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('popstate', this.handleHistoryChange);
  }

  handleHistoryChange() {
    const searchParams = new URLSearchParams(window.location.search);
    if (!searchParams.has('id') && this.state.groupId) {
      // Navigated back to search form
      this.resetState();
    } else if (searchParams.has('id') && !this.state.groupId) {
      this.handleIdChange(searchParams.get('id'));
    }
  }

  // Dummy function
  resetState() {}

  resetStatePreset(state: any) {
    this.setState(state);
  }

  handleIdChange(groupId: string | null, replaceState = false) {
    if (!groupId) {
      return;
    }

    this.setState({ groupId }, () => {
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set('id', groupId);
      const newRelativePathQuery = window.location.pathname + '?' + searchParams.toString();
      if (replaceState) {
        window.history.replaceState(null, '', newRelativePathQuery);
      } else {
        window.history.pushState(null, '', newRelativePathQuery);
      }
      this.loadInvoices();
    });
  }

  renderInvoiceLineDetails(lineDetails: InvoiceLineDetail[], addBorder = false) {
    return lineDetails.map((l, idx) => (
      <Tr key={l.id} isDetailLine>
        <Td leftBorder={addBorder} noBottomBorder={idx === lineDetails.length - 1} style={{textAlign: 'center'}}>{l.date.toLocaleDateString("en-US", { year: 'numeric',month: '2-digit', day:"2-digit" })}</Td>
        <Td noBottomBorder={idx === lineDetails.length - 1}>{l.description}</Td>
        <Td rightBorder={addBorder} noBottomBorder={idx === lineDetails.length - 1} style={{textAlign: 'center'}}>{l.amount === 0 ? '--' : l.amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</Td>
      </Tr>
    ));
  }

  renderInvoice(i: Invoice, index: number, invoices: Invoice[], addBorder = false) {
    return (
      <Fragment key={i.invoiceNumber}>
        <Tr whiteBg={index % 2 === 0} grayBg={index % 2 !== 0}>
          <Td leftBorder={addBorder} style={{textAlign: 'center'}} onClick={this.toggleInvoice.bind(this, (i.invoiceNumber))}>
            <input type="checkbox" disabled={!this.state.paypalInitComplete || this.state.interactionDisabled} checked={i.selected} onChange={this.handleSelectedChange.bind(this, i.invoiceNumber)} />
          </Td>
          <Td style={{textAlign: 'center'}} onClick={this.expandInvoice.bind(this, (i.invoiceNumber))}>{this.renderExpandIcon(i.expanded)}</Td>
          <Td>{i.invoiceNumber.toString()}</Td>
          <Td>{i.invoiceDisplayName}</Td>
          <Td style={{textAlign: 'right'}}>{i.totalAmount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</Td>
          <Td rightBorder={addBorder} style={{textAlign: 'right'}}>{i.outstandingAmount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</Td>
        </Tr>
        <Tr whiteBg>
          <Td colSpan={6} style={{padding: 0, borderWidth: 0}} bottomBorder={addBorder && index === invoices.length - 1}>
            <div style={{transition: 'max-height 0.5s', maxHeight: i.expanded ? '500px' : '0px', overflow: i.expanded ? 'auto' : 'hidden'}}>
              <Table style={{border: 0, marginBottom: 0}}>
                <tbody>
                  {this.renderInvoiceLineDetails(i.lineDetails, addBorder)}
                </tbody>
              </Table>
            </div>
          </Td>
        </Tr>
      </Fragment>
    );
  }

  renderInvoices() {
    const installmentInvoices = this.state.invoices?.filter(i => i.hasInstallmentPlan) || [];
    const regularInvoices = this.state.invoices?.filter(i => !i.hasInstallmentPlan) || [];

    if (installmentInvoices && installmentInvoices.length > 0) {
      return (
        <Fragment>
          <Tr whiteBg>
            <Td topBorder leftBorder rightBorder colSpan={6} subTitle>Installment Plan Status</Td>
          </Tr>
          {installmentInvoices.map((i, idx) => this.renderInvoice(i, idx, installmentInvoices, true))}
          <Tr whiteBg><Td colSpan={6} style={{ padding: '0' }}>&nbsp;</Td></Tr>
          <Tr whiteBg>
            <Td topBorder leftBorder rightBorder bottomBorder={regularInvoices.length === 0} colSpan={6} subTitle>Other Invoices{regularInvoices.length === 0 && ' – None'}</Td>
          </Tr>
          {regularInvoices.map((i, idx) => this.renderInvoice(i, idx, regularInvoices, true))}
        </Fragment>
      );
    }

    return regularInvoices.map((i, idx) => this.renderInvoice(i, idx, regularInvoices, false));
  }

  renderLoadingLines() {
    return [...Array(this.state.loadingLines)].map((e, i) =>
      <Tr key={i}>
        <Td><Shimmer /></Td>
        <Td><Shimmer /></Td>
        <Td><Shimmer /></Td>
        <Td><Shimmer /></Td>
        <Td><Shimmer /></Td>
        <Td><Shimmer /></Td>
      </Tr>
    );
  }

  renderTableHeader() {
    return <Tr grayBg>
      <Th onClick={() => this.props.isMobile && this.toggleAll()}>
        {this.props.isMobile ?
          <input type="checkbox" disabled={!this.state.paypalInitComplete} checked={this.state.allSelected} /> :
          <button type="button" disabled={!this.state.paypalInitComplete} onClick={this.toggleAll} style={{padding: '.25rem .5rem', fontSize: '0.8rem'}}>Select all</button>
        }
      </Th>
      <Th onClick={() => this.props.isMobile && this.expandAll()}>
        {this.state.allExpanded}
        {this.props.isMobile ?
          this.renderExpandIcon(this.state.allExpanded) :
          <button type="button" onClick={this.expandAll} style={{padding: '.25rem .5rem', fontSize: '0.8rem'}}>Expand all</button>
        }
      </Th>
      <Th>Invoice Number</Th>
      <Th>Invoice Name</Th>
      <Th>Total Invoice</Th>
      <Th>Balance Due</Th>
    </Tr>;
  }

  renderExpandIcon(expanded: boolean) {
    return <svg style={{width: '15px', height: '15px', transition: '0.25s', transform: expanded ? 'rotate(90deg)' : ''}} viewBox="0 0 574 1024">
      <path d="M10 9Q0 19 0 32t10 23l482 457L10 969Q0 979 0 992t10 23q10 9 24 9t24-9l506-480q10-10 10-23t-10-23L58 9Q48 0 34 0T10 9z"/>
    </svg>;
  }

  initPayPalButton() {
    (window as any).asdf = (window as any).paypal.Buttons({
      style: {
        color: 'white',
        shape: 'rect',
        label: 'pay',
        layout: 'vertical'
      },
      onInit: (data: any, actions: any) => {
        if (this.state.totalAmount === 0) {
          actions.disable();
        }
        actions.disable();
        this.setState({ paypalActions: actions, paypalInitComplete: true });
      },

      onClick: (a: any, b: any,c:any) => {
        if (this.state.totalAmount === 0) {
          this.setState({ invoiceSelectionError: true });
        }
      },

      createOrder: (data: any, actions: any) => {
        this.setState({ invoiceSelectionError: false, interactionDisabled: true });
        return actions.order.create({
          purchase_units: [{
            amount: {
              value: this.state.totalAmount,
              currency_code: 'USD',
              breakdown: {
                item_total: {
                  value: this.state.totalAmount,
                  currency_code: 'USD'
                }
              }
            },
            description: this.state.billingDisplayName,
            reference_id: this.state.groupId,
            items: this.state.invoices.filter(i => i.selected).map(i => ({
              name: `Invoice ${i.invoiceNumber}`,
              unit_amount: {
                value: i.outstandingAmount,
                currency_code: 'USD'
              },
              quantity: 1,
              description: i.invoiceDisplayName,
              sku: i.invoiceNumber
            }))
          }],
          application_context: {
            shipping_preference: 'NO_SHIPPING'
          }
        });
      },

      onApprove: (data: any, actions: any) => {
        return actions.order.capture().then((details: any) => {
          this.setState({ successMessage: `Transaction complete. Transaction ID: ${details.id}` });
        });
      },

      onError: (err: any) => {
        console.error(err);
        this.setState({
          generalError: 'Merchant error processing the payment, please try again or contact support.'
        });
      }
    }).render('#paypal-button-container')
  }

  render() {
    return (
      <div>
        {!this.state.groupId && <SearchForm onIdChange={this.handleIdChange}></SearchForm>}
        {this.state.generalError && <p style={{color: 'red', textAlign: 'center', fontWeight: 'bold'}}>{this.state.generalError}</p>}
        {this.state.successMessage &&
          <>
          <p style={{color: 'darkgreen', textAlign: 'center', fontWeight: 'bold'}}>{this.state.successMessage}</p>
          <p>Please note that all payments will be applied to the oldest outstanding invoice. For payments made on current invoices when older invoices are outstanding, an adjustment will appear on the original payment invoice and notes will be included in the Comments section of all invoices affected to explain the payment transfer.</p>
          <p>You will receive a confirmation email following your payment. Your member account will be updated with your payment and can be viewed in the member portal at www.ags.org within 72 hours.</p>
          <p>Thank you for your support of the American Gem Society. As always, we greatly value your membership. Please contact us at membership@ags.org or 866.805.6500 if you have any questions.</p>
          </>}
        {this.state.loading && !this.state.successMessage && !this.state.generalError && this.state.groupId &&
          <>
            <h3><Shimmer style={{maxWidth: '300px'}} /></h3>
              <div style={{maxWidth: '100%', overflowX: 'auto'}}>
                <Table id="invoicesTable">
                  <thead>
                    {this.renderTableHeader()}
                  </thead>
                  <tbody>
                    {this.renderLoadingLines()}
                    <Tr grayBg>
                      <Td colSpan={6} style={{textAlign: 'center'}}>
                        <strong>Payment amount:</strong>
                        <span> {this.state.totalAmount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</span>
                      </Td>
                    </Tr>
                  </tbody>
                </Table>
              </div>
              <p style={{color: 'red', textAlign: 'center', fontWeight: 'bold', visibility: this.state.invoiceSelectionError ? 'visible' : 'hidden'}}>Please select at least one invoice</p>
              <div id="paypal-button-container" style={{textAlign: 'center'}} />
          </>}
        {!this.state.loading && !this.state.successMessage &&
          <>
            <h3>{this.state.billingDisplayName}</h3>
            <div style={{maxWidth: '100%', overflowX: 'auto'}}>
              <Table id="invoicesTable">
                <thead>
                  {this.renderTableHeader()}
                </thead>
                <tbody>
                  {this.renderInvoices()}
                  <Tr grayBg>
                    <Td colSpan={6} style={{textAlign: 'center'}}>
                      <strong>Payment amount:</strong>
                      <span> {this.state.totalAmount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</span>
                    </Td>
                  </Tr>
                </tbody>
              </Table>
            </div>
            <p>Please note that all payments will be applied to the oldest outstanding invoice. For payments made on current invoices when older invoices are outstanding, an adjustment will appear on the original payment invoice and notes will be included in the Comments section of all invoices affected to explain the payment transfer.</p>
            <p>You will receive a confirmation email following your payment. Your member account will be updated with your payment and can be viewed in the member portal at www.ags.org within 72 hours.</p>
            <p style={{color: 'red', textAlign: 'center', fontWeight: 'bold', visibility: this.state.invoiceSelectionError ? 'visible' : 'hidden'}}>Please select at least one invoice</p>
            <div id="paypal-button-container" style={{textAlign: 'center'}} />
            <p>Thank you for your support of the American Gem Society. As always, we greatly value your membership. Please contact us at membership@ags.org or 866.805.6500 if you have any questions.</p>
          </>
        }
      </div>
    );
  }
}

function AppHookWrapper() {
    const isMobile = useMediaQuery('(max-width: 600px)');
    return <App isMobile={isMobile} />;
}

export default AppHookWrapper;
